import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getBanks, saveBank, generateBankCode } from "../../components/Api/GmsBank";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  bankId: null,
  bankCode: "",
  bankNameEn: "",
  bankNameAr: "",
  status: true,
  createdBy: 1,
  createdOn: new Date().toISOString(),
  modifiedBy: null,
  modifiedOn: null,
};

const emptyFilter = {
  bankId: null,
  bankCode: "",
  bankNameEn: "",
  bankNameAr: "",
  status: null,
};

export default function BankPage() {
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Get user session
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const session = getUserSession();

  const formatDateTime = (dateTimeStr) => {
    try {
      if (!dateTimeStr) return "N/A";
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBanks();
    }
  }, [permissionsLoading, canView, page, pageSize]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsBank');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsBank');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsBank');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadBanks = async (filterData = {}) => {
    setLoading(true);
    try {
      const filterPayload = {
        ...filterData,
        page: page,
        pageSize: pageSize,
      };
      const response = await getBanks(filterPayload);
      setBanks(response?.banks || []);
      setTotalCount(response?.totalCount || 0);
    } catch (error) {
      Swal.fire("Error", "Failed to load banks: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!form.bankNameEn || form.bankNameEn.trim() === "") {
        Swal.fire("Error", "Bank Name (English) is required", "error");
        setLoading(false);
        return;
      }

      // Auto-generate bank code if not provided
      let bankCode = form.bankCode?.trim();
      if (!bankCode && !isEditing) {
        try {
          const codeResult = await generateBankCode();
          if (codeResult.isSuccess) {
            bankCode = codeResult.bankCode;
          } else {
            Swal.fire("Error", codeResult.message || "Failed to generate bank code", "error");
            setLoading(false);
            return;
          }
        } catch (error) {
          Swal.fire("Error", "Failed to generate bank code", "error");
          setLoading(false);
          return;
        }
      }

      // Prepare save data - remove ID field for inserts
      const bankData = {
        ...form,
        bankCode: bankCode || form.bankCode,
        createdBy: isEditing ? form.createdBy : session.userId,
        modifiedBy: isEditing ? session.userId : null,
        modifiedOn: isEditing ? new Date().toISOString() : null,
      };
      
      // Remove bankId if it's null, 0, or undefined (insert operation)
      if (!bankData.bankId || bankData.bankId === 0) {
        delete bankData.bankId;
      }

      const result = await saveBank(bankData);

      if (result.id > 0) {
        Swal.fire("Success", result.message || "Bank saved successfully", "success");
        setForm(defaultForm);
        setIsEditing(false);
        loadBanks(filter);
      } else {
        Swal.fire("Error", result.message || "Failed to save bank", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to save bank", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (bank) => {
    try {
      setViewLoadingRow(bank.bankId || bank.BankId);
      setViewData(bank);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire("Error", "Failed to view bank details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (bank) => {
    const bankId = bank.bankId || bank.BankId;
    setForm({
      bankId: bankId,
      bankCode: bank.bankCode || bank.BankCode || "",
      bankNameEn: bank.bankNameEn || bank.BankNameEn || "",
      bankNameAr: bank.bankNameAr || bank.BankNameAr || "",
      status: bank.status !== undefined ? bank.status : (bank.Status !== undefined ? bank.Status : true),
      createdBy: bank.createdBy || bank.CreatedBy || session.userId,
      createdOn: bank.createdOn || bank.CreatedOn || new Date().toISOString(),
      modifiedBy: null,
      modifiedOn: null,
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };


  const handleApplyFilter = () => {
    setPage(1);
    loadBanks(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadBanks(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Options for searchable dropdowns
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Bank Management" breadcrumbItem="Bank Management" />
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Bank" : "Add New Bank"}
                      </h5>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Bank Code</Label>
                              <Input
                                name="bankCode"
                                value={form.bankCode}
                                onChange={(e) => handleFormChange("bankCode", e.target.value)}
                                placeholder="Leave empty to auto-generate"
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Name (EN) <span className="text-danger">*</span></Label>
                              <Input
                                name="bankNameEn"
                                value={form.bankNameEn}
                                onChange={(e) => handleFormChange("bankNameEn", e.target.value)}
                                required
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Name (AR)</Label>
                              <Input
                                name="bankNameAr"
                                value={form.bankNameAr}
                                onChange={(e) => handleFormChange("bankNameAr", e.target.value)}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <div className="form-check form-switch">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="bank_status"
                                name="status"
                                checked={form.status}
                                onChange={(e) => handleFormChange("status", e.target.checked)}
                                disabled={!canDelete}
                              />
                              <label className="form-check-label" htmlFor="bank_status">
                                {form.status ? "Active" : "Inactive"}
                              </label>
                            </div>
                          </Col>
                        </Row>

                        <div className="d-flex gap-2">
                          <Button
                            type="submit"
                            color="success"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <ClipLoader size={20} color="white" />{" "}
                                <span className="ms-2">Saving...</span>
                              </>
                            ) : isEditing ? (
                              "Update Bank"
                            ) : (
                              "Save Bank"
                            )}
                          </Button>
                          {isEditing && (
                            <Button
                              type="button"
                              color="secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </Form>
                    </CardBody>
                  </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Banks</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Bank Code</Label>
                              <Input
                                type="text"
                                value={filter.bankCode}
                                onChange={(e) => handleFilterChange("bankCode", e.target.value)}
                                placeholder="Search bank code..."
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (EN)</Label>
                              <Input
                                type="text"
                                value={filter.bankNameEn}
                                onChange={(e) => handleFilterChange("bankNameEn", e.target.value)}
                                placeholder="Search name (EN)..."
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (AR)</Label>
                              <Input
                                type="text"
                                value={filter.bankNameAr}
                                onChange={(e) => handleFilterChange("bankNameAr", e.target.value)}
                                placeholder="Search name (AR)..."
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Status</Label>
                              <Select
                                isClearable
                                value={
                                  filter.status !== null
                                    ? statusOptions.find(
                                        (option) => option.value === filter.status
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange("status", option ? option.value : null)
                                }
                                options={statusOptions}
                                styles={selectStyles}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply Filter
                          </Button>
                          <Button color="secondary" onClick={handleClearFilter}>
                            Clear Filter
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Banks List */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">Banks List ({totalCount} records)</h5>
                    {loading ? (
                      <div className="text-center py-4">
                        <ClipLoader color="#556ee6" loading={loading} size={50} />
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Bank Code</th>
                                <th>Name (EN)</th>
                                <th>Name (AR)</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {banks.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    No banks found
                                  </td>
                                </tr>
                              ) : (
                                banks.map((bank, index) => {
                                  const bankId = bank.bankId || bank.BankId;
                                  const bankCode = bank.bankCode || bank.BankCode || 'N/A';
                                  const bankNameEn = bank.bankNameEn || bank.BankNameEn || 'N/A';
                                  const bankNameAr = bank.bankNameAr || bank.BankNameAr || 'N/A';
                                  const status = bank.status !== undefined ? bank.status : (bank.Status !== undefined ? bank.Status : true);
                                  
                                  return (
                                    <tr key={bankId}>
                                      <td>{(page - 1) * pageSize + index + 1}</td>
                                      <td>{bankCode}</td>
                                      <td>{bankNameEn}</td>
                                      <td>{bankNameAr}</td>
                                      <td>
                                        <span className={`badge ${status ? 'bg-success' : 'bg-danger'}`}>
                                          {status ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleView(bank)}
                                            disabled={viewLoadingRow === bankId}
                                          >
                                            {viewLoadingRow === bankId ? (
                                              <ClipLoader size={16} color="#ffffff" />
                                            ) : (
                                              <Eye size={16} />
                                            )}
                                          </Button>
                                          {canEdit && (
                                            <Button
                                              color="warning"
                                              size="sm"
                                              onClick={() => handleEdit(bank)}
                                              disabled={editLoadingRow === bankId}
                                            >
                                              {editLoadingRow === bankId ? (
                                                <ClipLoader size={16} color="#ffffff" />
                                              ) : (
                                                <SquarePen size={16} />
                                              )}
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                        {totalCount > 0 && <Pagination />}
                      </>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* View Modal */}
        <Modal isOpen={showViewModal} toggle={() => setShowViewModal(false)} size="lg">
          <ModalHeader toggle={() => setShowViewModal(false)}>Bank Details</ModalHeader>
          <ModalBody>
            {viewData && (
              <Row>
                <Col md={6}>
                  <p><strong>Bank Code:</strong> {viewData.bankCode || viewData.BankCode || 'N/A'}</p>
                  <p><strong>Name (EN):</strong> {viewData.bankNameEn || viewData.BankNameEn || 'N/A'}</p>
                  <p><strong>Name (AR):</strong> {viewData.bankNameAr || viewData.BankNameAr || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> 
                    <span className={`badge ms-2 ${(viewData.status !== undefined ? viewData.status : (viewData.Status !== undefined ? viewData.Status : true)) ? 'bg-success' : 'bg-danger'}`}>
                      {(viewData.status !== undefined ? viewData.status : (viewData.Status !== undefined ? viewData.Status : true)) ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                  <p><strong>Created On:</strong> {formatDateTime(viewData.createdOn || viewData.CreatedOn)}</p>
                  <p><strong>Modified On:</strong> {formatDateTime(viewData.modifiedOn || viewData.ModifiedOn)}</p>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

